import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/*
 * Rellevo: gaveta lateral do carrinho (estilo woodora.world)
 * - Atualiza o conteúdo quando um produto é adicionado
 * - Botões + / − / lixeira alteram o carrinho sem sair da página
 * - Contador de reserva de 15 minutos (reinicia ao recarregar a página)
 */

const SECTION_ID = 'cart-drawer-section';
const SOURCE = 'rellevo-cart';
const TIMER_KEY = 'rellevo_vres_start';

/* ---------- Contador da reserva ---------- */
(function initReservationTimer() {
  if (window.__rellevoTimerInit) return;
  window.__rellevoTimerInit = true;

  let isReload = false;
  try {
    const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    if (nav && nav.type === 'reload') isReload = true;
  } catch (e) {}

  let start = null;
  try {
    start = sessionStorage.getItem(TIMER_KEY);
  } catch (e) {}

  if (isReload || !start) {
    start = String(Date.now());
    try {
      sessionStorage.setItem(TIMER_KEY, start);
    } catch (e) {}
  }
  const startMs = parseInt(start, 10);

  const fmt = (s) => {
    const m = Math.floor(s / 60);
    const ss = s % 60;
    return (m < 10 ? '0' + m : m) + ':' + (ss < 10 ? '0' + ss : ss);
  };

  const tick = () => {
    const el = document.querySelector('#cart-drawer [data-rellevo-timer]');
    if (!el) return;
    const cart = el.closest('rellevo-cart');
    const duration = Number(cart?.dataset.reservationSeconds) || 900;
    const left = Math.max(0, duration - Math.floor((Date.now() - startMs) / 1000));
    el.textContent = fmt(left);
  };

  tick();
  setInterval(tick, 500);
  window.addEventListener('rellevo:timer', tick);
})();

/* ---------- Gaveta ---------- */
class RellevoCart extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    window.addEventListener('pageshow', this.#onPageShow);
  }

  /** Ao voltar do checkout pelo botão "voltar", recarrega o carrinho */
  #onPageShow = (event) => {
    if (event.persisted) this.#refresh();
  };

  disconnectedCallback() {
    window.removeEventListener('pageshow', this.#onPageShow);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
  }

  get #drawer() {
    return /** @type {any} */ (this.closest('theme-drawer'));
  }

  /** Quando outro lugar do tema (ex.: botão "Ajouter au panier") muda o carrinho */
  #onCartLinesUpdate = (event) => {
    if (event.target === this) return;
    this.classList.add('is-refreshing');

    event.promise
      ?.then((result) => {
        if (result?.detail?.didError) {
          this.classList.remove('is-refreshing');
          return;
        }
        this.#refresh();
      })
      .catch(() => this.classList.remove('is-refreshing'));
  };

  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    if (target.closest('[data-rellevo-close]')) {
      event.preventDefault();
      this.#drawer?.close();
      return;
    }

    const item = /** @type {HTMLElement | null} */ (target.closest('.rellevo-cart__item'));
    if (!item) return;
    const input = /** @type {HTMLInputElement} */ (item.querySelector('[data-rellevo-qty]'));
    const current = parseInt(input.value, 10) || 0;

    if (target.closest('[data-rellevo-plus]')) {
      this.#update(item, current + 1);
    } else if (target.closest('[data-rellevo-minus]')) {
      this.#update(item, Math.max(0, current - 1));
    } else if (target.closest('[data-rellevo-remove]')) {
      this.#update(item, 0);
    }
  };

  #onChange = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (!input.matches('[data-rellevo-qty]')) return;
    const item = /** @type {HTMLElement} */ (input.closest('.rellevo-cart__item'));
    this.#update(item, Math.max(0, parseInt(input.value, 10) || 0));
  };

  async #update(item, quantity) {
    item.classList.add('is-loading');

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: item.dataset.key, quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: item.dataset.key, quantity, sections: [SECTION_ID] }),
      });
      const cart = await response.json();

      if (!response.ok || cart.status) {
        item.classList.remove('is-loading');
        deferred.reject(new Error(cart.description || cart.message || 'Cart update failed'));
        await this.#refresh();
        return;
      }

      this.#render(cart.sections?.[SECTION_ID]);

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          source: SOURCE,
          didError: false,
        },
      });
    } catch (error) {
      console.error(error);
      item.classList.remove('is-loading');
      deferred.reject(error);
    }
  }

  async #refresh() {
    try {
      const url = `${window.location.pathname}?sections=${SECTION_ID}`;
      const response = await fetch(url);
      const data = await response.json();
      this.#render(data[SECTION_ID]);
    } catch (error) {
      console.error(error);
      this.classList.remove('is-refreshing');
    }
  }

  #render(html) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('rellevo-cart');
    if (!fresh) return;

    const body = this.querySelector('.rellevo-cart__body');
    const scrollTop = body ? body.scrollTop : 0;

    this.className = fresh.className; // também remove 'is-refreshing'
    this.innerHTML = fresh.innerHTML;

    const newBody = this.querySelector('.rellevo-cart__body');
    if (newBody) newBody.scrollTop = scrollTop;

    // Atualiza o contador na hora, sem esperar o próximo segundo
    window.dispatchEvent(new Event('rellevo:timer'));
  }
}

if (!customElements.get('rellevo-cart')) {
  customElements.define('rellevo-cart', RellevoCart);
}
